import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { competencyKeys, roleKeys } from "@/lib/query";
import type {
  CompetencyCreateInput,
  CompetencyStatus,
  CompetencyUpdateInput,
} from "@/schemas/competencies";

export interface Competency {
  id: string;
  name: string;
  description: string | null;
  /** Many-to-many as of the category/theme merge — a competency can carry several. */
  categories: { id: string; name: string }[];
  // The roles that require this competency, as {id, name}, for linking. Empty when none.
  roles: { id: string; name: string }[];
  status: CompetencyStatus;
  createdAt: string;
  updatedAt: string;
}

interface CompetenciesListResponse {
  data: Competency[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

interface PageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

interface Filters {
  search: string;
  statuses: string[];
  sort?: string;
  dir?: "asc" | "desc";
}

export function useCompetencies(filters: Filters, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...competencyKeys.list(filters), pageRequest] as const,
    queryFn: async (): Promise<CompetenciesListResponse> => {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (pageRequest.cursor) {
        params.set("cursor", pageRequest.cursor);
        params.set("direction", pageRequest.direction);
      }
      if (filters.search) params.set("search", filters.search);
      if (filters.statuses.length) params.set("status", filters.statuses.join(","));
      if (filters.sort) {
        params.set("sort", filters.sort);
        params.set("dir", filters.dir ?? "asc");
      }
      const res = await fetch(`/api/competencies?${params}`);
      if (!res.ok) throw new Error("Failed to fetch competencies");
      return res.json();
    },
  });
}

export function useCompetency(id: string) {
  return useQuery({
    queryKey: competencyKeys.detail(id),
    queryFn: async (): Promise<Competency | null> => {
      const res = await fetch(`/api/competencies/${id}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch competency");
      const json = await res.json();
      return json.data;
    },
  });
}

export interface CompetencyPerson {
  id: string;
  name: string;
  title: string | null;
  /** The role name(s) via which the person has this competency. */
  roles: string[];
}

/** People who have this competency, derived from the roles they hold that require it. */
export function useCompetencyPeople(competencyId: string) {
  return useQuery({
    queryKey: competencyKeys.people(competencyId),
    queryFn: async (): Promise<CompetencyPerson[]> => {
      const res = await fetch(`/api/competencies/${competencyId}/people`);
      if (!res.ok) throw new Error("Failed to fetch people with this competency");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateCompetency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CompetencyCreateInput) => {
      const res = await fetch("/api/competencies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Create failed");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: competencyKeys.all });
      toast.success("Competency created");
    },
    onError: () => toast.error("Failed to create competency"),
  });
}

export function useUpdateCompetency(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CompetencyUpdateInput) => {
      const res = await fetch(`/api/competencies/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Update failed");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: competencyKeys.all });
      // Role requirement lists denormalize competencyName — refresh roles so a
      // rename doesn't leave a stale competency label on role detail pages.
      qc.invalidateQueries({ queryKey: roleKeys.all });
      toast.success("Competency updated");
    },
    onError: () => toast.error("Failed to update competency"),
  });
}
